import Analytics from '../analytics';
import Environment from '../environment';
import { getLogger } from '../logging/logger';
import AudioManager from '../manager/audioManager';
import { addReadyPromise } from '../util/utilities';

/**
 * @typedef {object} IntentOptions
 * @property {string} action
 * @property {string} [url]
 * @property {number} [requestCode]
 * @property {Record<string, any>} [extras]
 *
 * @typedef {object} FilterOptions
 * @property {string[]} filterActions
 *
 * @typedef {(intent: IntentOptions) => void} IntentHandler
 */

const logger = getLogger('Cordova');

const { isReady, onReadyReject, onReadyResolve } = addReadyPromise();

class Android {
	constructor() {
		logger.log('[Constructor]');

		this.isReady = isReady;

		this.registeredReceivers = new Map();
		this.intentCallback = null;

		if (Environment.isCordova() && !Environment.isInHub()) {
			this.loadLibrary();
			// Handle Android lifecycle events
		}
	}

	loadLibrary() {
		logger.log(`[${this.loadLibrary.name}]`);

		const script = document.createElement('script');
		script.async = true;
		script.src = `//localhost/cordova.js`;
		script.addEventListener('error', this.onLoadError.bind(this));
		document.addEventListener('deviceready', () => {
			document.addEventListener('pause', this.onPause.bind(this));
			document.addEventListener('resume', this.onResume.bind(this));
			onReadyResolve(this);
		});
		document.head.appendChild(script);
	}

	/**
	 * @param {ErrorEvent} event
	 */
	onLoadError(event) {
		logger.error(`[${this.onLoadError.name} Error]`, { event });

		onReadyReject(event);
	}

	onPause() {
		logger.log(`[${this.onPause.name}]`);

		AudioManager.stopAllAudio();
		Analytics.sendEvent('AppLifecycle', { status: 'paused' });
	}

	onResume() {
		logger.log(`[${this.onResume.name}]`);

		AudioManager.resumeBackgroundMusic();
		Analytics.sendEvent('AppLifecycle', { status: 'resumed' });
	}

	exitApp() {
		logger.log(`[${this.exitApp.name}]`);

		// @ts-ignore
		window.navigator.app?.exitApp();
	}

	/**
	 *
	 * @returns {Promise<CordovaPlugins>}
	 */
	getPlugins() {
		logger.debug(`[${this.getPlugins.name}]`);

		// @ts-ignore
		if (window.plugins) {
			// @ts-ignore
			return Promise.resolve(window.plugins);
		}

		return Promise.reject(new Error('Cordova Plugins not available'));
	}

	getIntentShim() {
		logger.debug(`[${this.getIntentShim.name}]`);

		return this.getPlugins().then((plugins) => {
			if (plugins.intentShim) {
				return plugins.intentShim;
			}

			throw new Error('Cordova IntentShim Plugin not available');
		});
	}

	/**
	 * Listens a global listener for any broadcast intent on Android
	 * @param {FilterOptions} filters Multiple filters to filter the intent
	 * @param {(options: IntentOptions) => void} callback The callback to handle a broadcast
	 */
	async registerBroadcastReceiver(filters, callback) {
		logger.log(`[${this.registerBroadcastReceiver.name}]`, { filters });

		return this.getIntentShim().then((intentShim) => {
			this.registeredReceivers.set(filters, callback);
			intentShim.registerBroadcastReceiver(filters, callback);
		});
	}

	/**
	 * Unregisters all broadcast receivers
	 */
	unregisterBroadcastReceiver() {
		logger.log(`[${this.unregisterBroadcastReceiver.name}]`);

		return this.getIntentShim().then((intentShim) => {
			this.registeredReceivers.clear();
			intentShim.unregisterBroadcastReceiver();
		});
	}

	/**
	 * @param {IntentOptions} intent
	 * @returns {Promise<any>}
	 */
	sendBroadcast(intent) {
		logger.log(`[${this.sendBroadcast.name}]`, { intent });

		return this.getIntentShim().then(
			(intentShim) =>
				new Promise((resolve, reject) => {
					intentShim.sendBroadcast(intent, resolve, (/** @type {Error} */ error) => {
						logger.error(`[${this.sendBroadcast.name}]`, { error });
						reject(error);
					});
				}),
		);
	}

	/**
	 * @param {(intent: IntentOptions) => void} callback
	 */
	onIntent(callback) {
		logger.log(`[${this.onIntent.name}]`);

		this.intentCallback = callback;
		return this.getIntentShim().then((intentShim) => {
			intentShim.onIntent(callback);
		});
	}

	/**
	 * @param {IntentOptions} intent
	 * @returns {Promise<any>}
	 */
	startActivity(intent) {
		logger.log(`[${this.startActivity.name}]`, { intent });

		return this.getIntentShim()
			.then((intentShim) =>
				Analytics.sendEvent('CordovaStartActivity', intent).then(
					() =>
						new Promise((resolve, reject) =>
							intentShim.startActivity(intent, resolve, reject),
						),
				),
			)
			.catch((/** @type {Error} */ error) => {
				logger.error(`[${this.startActivity.name} Error]`, { error });
				Analytics.sendEvent('CordovaStartActivity-Error', { error });
				return Promise.reject(error);
			});
	}

	/**
	 * @param {IntentOptions} intent
	 * @returns {Promise<IntentOptions>}
	 */
	startActivityForResult(intent) {
		logger.log(`[${this.startActivityForResult.name}]`, { intent });

		return new Promise((resolve, reject) => {
			if (window.cordova?.plugins?.intentShim) {
				window.cordova.plugins.intentShim.startActivityForResult(intent, resolve, reject);
			} else {
				reject(new Error('Cordova intentShim not available'));
			}
		});
	}

	/**
	 * @returns {Promise<any>}
	 */
	getIntent() {
		logger.log(`[${this.getIntent.name}]`);

		return this.getIntentShim().then(
			(intentShim) => new Promise((resolve, reject) => intentShim.getIntent(resolve, reject)),
		);
	}

	/**
	 * @param {IntentOptions} intentResult
	 * @returns {Promise<IntentOptions>}
	 */
	sendResult(intentResult) {
		logger.log(`[${this.sendResult.name}]`, { intentResult });

		return this.getIntentShim().then(
			(intentShim) => new Promise((resolve) => intentShim.sendResult(intentResult, resolve)),
		);
	}

	/**
	 * @param {string} packageName
	 * @returns {Promise<boolean>}
	 */
	packageExists(packageName) {
		logger.log(`[${this.packageExists.name}]`, { packageName });

		return this.getIntentShim().then(
			(intentShim) =>
				new Promise((resolve) => intentShim.packageExists(packageName, resolve)),
		);
	}
}

export default new Android();
